/* Phase 35 — Plating Intelligence page (sidebar entry point).
 * Lets the user pick any of their recipes, then runs the wizard.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Search, UtensilsCrossed, Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../api";
import { Button, Input, Card, EmptyState, Spinner } from "../components/ui/Primitives";
import PlatingWizard from "../components/PlatingWizard";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

export default function PlatingIntelligencePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [search, setSearch] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const hasModule = (user?.modules || []).includes("plating");

  useEffect(() => {
    api.get("/recipes").then(({ data }) => setRecipes(data.items || [])).catch(() => {}).finally(() => setLoading(false));
    if (hasModule) api.get("/plating/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [hasModule]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? recipes.filter((r) => r.name?.toLowerCase().includes(q)) : recipes;
  }, [recipes, search]);

  const active = recipes.find((r) => r.id === activeId);

  // --- Locked state: Küche plan without add-on ---
  if (!hasModule) {
    return <PlatingLockedState plan={user?.plan} />;
  }

  return (
    <div className="space-y-6" data-testid="plating-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Plating Intelligence</h1>
          <p className="text-sm text-ink-muted mt-1">
            Professionelle Anrichte-Vorschläge mit ultra-realistischen Bildern, basierend auf deinen Rezepten.
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[320px_1fr] gap-6">
        <Card className="p-3 space-y-3 lg:max-h-[80vh] lg:overflow-y-auto">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-ink-muted" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rezept suchen…"
              className="pl-8"
              data-testid="plating-recipe-search"
            />
          </div>
          {loading ? (
            <div className="py-6 text-center"><Spinner /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={UtensilsCrossed} title="Keine Rezepte" description="Lege zuerst ein Rezept an." />
          ) : (
            <div className="space-y-1">
              {filtered.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setActiveId(r.id)}
                  data-testid={`plating-recipe-pick-${r.id}`}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition ${
                    activeId === r.id ? "bg-brand/10 text-brand" : "hover:bg-white/5"
                  }`}
                >
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-[11px] text-ink-muted truncate">{r.category}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        <div>
          {active ? (
            <Card className="p-5">
              <div className="mb-4">
                <div className="text-xs uppercase tracking-wide text-ink-muted">Ausgewähltes Rezept</div>
                <div className="text-lg font-medium">{active.name}</div>
              </div>
              <PlatingWizard recipe={active} />
            </Card>
          ) : (
            <Card className="p-12">
              <EmptyState
                icon={Sparkles}
                title="Wähle ein Rezept aus"
                description="Plating Intelligence erstellt aus den vorhandenen Zutaten Anrichte-Vorschläge und Fotos."
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function PlatingLockedState({ plan }) {
  // Bar plan: not relevant. Küche plan: show add-on offer.
  const isKueche = plan === "kueche";
  const buyAddon = async () => {
    try {
      const { data } = await api.post("/billing/plating-addon/checkout", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="max-w-2xl mx-auto py-12 space-y-6" data-testid="plating-locked">
      <div className="text-center space-y-2">
        <UtensilsCrossed size={36} className="mx-auto text-brand" />
        <h1 className="text-2xl md:text-3xl font-semibold">Plating Intelligence</h1>
        <p className="text-sm text-ink-muted">
          Professionelle Anrichte-Vorschläge und ultra-realistische Foodfotos für deine Speisekarte.
        </p>
      </div>
      {isKueche ? (
        <Card className="p-6 space-y-4">
          <div className="text-sm">
            Plating Intelligence ist im <span className="font-medium">Küche-Plan</span> nicht enthalten. Du hast zwei Optionen:
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => window.location.assign("/billing")}
              className="text-left p-4 rounded-lg border border-white/10 hover:border-brand transition"
              data-testid="plating-upgrade-pro"
            >
              <div className="text-sm font-semibold">Auf Pro upgraden</div>
              <div className="text-xs text-ink-muted mt-1">€129/Monat · 60 AI-Credits + 50 Plating-Credits inklusive · alle Module</div>
            </button>
            <button
              type="button"
              onClick={buyAddon}
              className="text-left p-4 rounded-lg border border-brand/40 bg-brand/5 hover:bg-brand/10 transition"
              data-testid="plating-buy-addon"
            >
              <div className="text-sm font-semibold">Add-on buchen</div>
              <div className="text-xs text-ink-muted mt-1">€19/Monat · 20 Plating-Credits/Monat · ohne Plan-Wechsel</div>
            </button>
          </div>
        </Card>
      ) : (
        <Card className="p-6 text-sm text-ink-muted">
          Plating Intelligence ist in deinem aktuellen Plan nicht verfügbar.
        </Card>
      )}
    </div>
  );
}
